import { Injectable, NgZone, RendererFactory2, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import Clarity from '@microsoft/clarity';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MyAnalyticsConsentService } from '../../../api/api-services/my-analytics-consent.service';

/** The visitor's decision on analytics cookies, kept in localStorage under `analyticsConsent`. */
export type AnalyticsConsent = 'granted' | 'denied';

/**
 * Address of the panel's privacy policy, linked from the consent bar and the "Prywatność" page.
 * The panel has no such page yet, so nothing is linked until an address is put here.
 */
export const PRIVACY_POLICY_URL: string | null = null;

/** Attribute Clarity reads to leave an element (and its children) readable under Strict masking. */
export const CLARITY_UNMASK_ATTRIBUTE = 'data-clarity-unmask';

const CONSENT_STORAGE_KEY = 'analyticsConsent';
/** `<account GUID>:<decision>` last accepted by the API, so each decision of an account is sent once. */
const CONSENT_REPORTED_STORAGE_KEY = 'analyticsConsentReported';
/** How the API tells the panel's decisions from those of other apps. */
const CONSENT_APP = 'panel';
const AUTH_TOKEN_STORAGE_KEY = 'authToken';
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Roles the `role` tag may take, in order of precedence when a token carries more than one. */
const KNOWN_ROLES = ['PlatformOperator', 'Osk', 'OskOffice', 'OskInstructor', 'OskLecturer'];

/**
 * Parts of the fas-admin layout that are rendered by the library, so the unmask attribute is added after render:
 * the side menu (route labels only) and the "Moje konto" menu (static labels). The top bar stays masked - it shows
 * the account's display name.
 */
const LIBRARY_UNMASK_SELECTOR = '.side-bar, .account-menu';

/** Claims of the login token this service reads: `sub` is the user account GUID, `Roles` is `;`-separated. */
interface AuthTokenClaims {
  sub?: string;
  Roles?: string | string[];
}

interface UserIdentity {
  userGuid: string;
  role: string;
}

/**
 * Microsoft Clarity session recording for the panel. Started once from AppComponent; does nothing when the
 * environment has no `clarityProjectId` (development builds).
 *
 * Recording runs cookieless until the visitor grants consent on the bottom bar; the decision can be changed
 * later on the "Prywatność" page. The decision of a signed-in account is also sent to the API, which shows it to
 * the operator in the OSK statistics. Route changes are tracked by Clarity itself.
 */
@Injectable({ providedIn: 'root' })
export class ClarityService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly renderer = inject(RendererFactory2).createRenderer(null, null);
  private readonly myAnalyticsConsentService = inject(MyAnalyticsConsentService);

  /** True only where a Clarity project id is configured. */
  readonly enabled = environment.clarityProjectId !== '';

  private readonly consentSubject = new BehaviorSubject<AnalyticsConsent | null>(readStoredConsent());
  /** The stored decision; null until the visitor decides. */
  readonly consent$ = this.consentSubject.asObservable();

  private started = false;
  private identifiedToken: string | null = null;

  get consent(): AnalyticsConsent | null {
    return this.consentSubject.value;
  }

  /** Loads Clarity, applies the stored consent and starts following the signed-in account. Safe to call twice. */
  start(): void {
    if (this.started || !this.enabled) {
      return;
    }
    this.started = true;

    Clarity.init(environment.clarityProjectId);
    Clarity.setTag('app', 'panel');
    Clarity.setTag('buildNumber', environment.buildNumber);
    this.applyConsent(this.consent);

    this.syncIdentity();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.syncIdentity());

    this.unmaskLibraryLayout();
  }

  setConsent(decision: AnalyticsConsent): void {
    localStorage.setItem(CONSENT_STORAGE_KEY, decision);
    this.consentSubject.next(decision);
    if (this.started) {
      this.applyConsent(decision);
    }
    this.reportConsent();
  }

  private applyConsent(decision: AnalyticsConsent | null): void {
    // No decision yet counts as denied: Clarity keeps recording without cookies until the visitor agrees.
    Clarity.consentV2({
      ad_Storage: 'denied',
      analytics_Storage: decision === 'granted' ? 'granted' : 'denied',
    });
  }

  /**
   * Ties the recording to the signed-in account (its GUID, hashed by Clarity on the client) and tags its role.
   * Runs after every navigation, so signing in or out during the session, or switching accounts, is picked up:
   * identify is repeated on every page as Clarity recommends, the role tag only when the token changes.
   */
  private syncIdentity(): void {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      this.identifiedToken = null;
      return;
    }
    const identity = readIdentity(token);
    if (!identity) {
      return;
    }
    Clarity.identify(identity.userGuid);
    if (token !== this.identifiedToken) {
      Clarity.setTag('role', identity.role);
      this.identifiedToken = token;
      this.reportConsent();
    }
  }

  /**
   * Sends the decision of the signed-in account to the API. Runs when the decision is made and when an account
   * signs in (or the panel starts signed in), so a decision made before signing in, or before the API recorded
   * decisions, still arrives. Each account and decision is sent once; a failed call is repeated on the next start.
   */
  private reportConsent(): void {
    const decision = this.consent;
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const identity = token ? readIdentity(token) : null;
    if (!decision || !identity) {
      return;
    }
    const reported = `${identity.userGuid}:${decision}`;
    if (localStorage.getItem(CONSENT_REPORTED_STORAGE_KEY) === reported) {
      return;
    }
    this.myAnalyticsConsentService.put({ app: CONSENT_APP, granted: decision === 'granted' }).subscribe({
      next: () => localStorage.setItem(CONSENT_REPORTED_STORAGE_KEY, reported),
      // Not remembered, so the next start tries again; recording itself does not depend on it.
      error: () => undefined,
    });
  }

  /**
   * fas-admin renders the side menu and the account menu from the library, so their templates cannot carry
   * `data-clarity-unmask`. The attribute is set after render instead and re-applied whenever the DOM changes
   * (the menu is re-created after login and when the sidebar is toggled). Runs outside the zone: DOM
   * mutations are frequent and none of this needs change detection.
   */
  private unmaskLibraryLayout(): void {
    const apply = () => {
      document.querySelectorAll<HTMLElement>(LIBRARY_UNMASK_SELECTOR).forEach((element) => {
        if (element.getAttribute(CLARITY_UNMASK_ATTRIBUTE) !== 'true') {
          this.renderer.setAttribute(element, CLARITY_UNMASK_ATTRIBUTE, 'true');
        }
      });
    };
    this.zone.runOutsideAngular(() => {
      apply();
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) {
          return;
        }
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          apply();
        });
      }).observe(document.body, { childList: true, subtree: true });
    });
  }
}

function readStoredConsent(): AnalyticsConsent | null {
  const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
  return stored === 'granted' || stored === 'denied' ? stored : null;
}

/** The account GUID and role from the login token, or null when the token cannot be read or has no GUID. */
function readIdentity(token: string): UserIdentity | null {
  try {
    const claims = jwtDecode<AuthTokenClaims>(token);
    // Only ever send the GUID, never an e-mail or another readable identifier.
    if (!claims.sub || !GUID_PATTERN.test(claims.sub)) {
      return null;
    }
    const roles = Array.isArray(claims.Roles) ? claims.Roles : (claims.Roles ?? '').split(';');
    const role = KNOWN_ROLES.find((known) => roles.includes(known)) ?? roles.find((value) => value !== '') ?? 'unknown';
    return { userGuid: claims.sub, role };
  } catch {
    return null;
  }
}
